import {Circle, GoogleMap} from '@react-google-maps/api'
import type {JSX} from 'react'
import React, {useRef} from 'react'
import {requireHexCode} from '@/products/flexible-web-toolkit/colors'

interface MapProps {
	containerClassName: string
	initialZoom: number
	maxZoom: number
	minZoom?: number
	onClick: (e: google.maps.MapMouseEvent) => void
	onLoad: (map: google.maps.Map) => void
	containerStyle?: React.CSSProperties
	children?: React.ReactNode
	radius: number
	searchAreaAccentColour: string
	searchAreaOpacity: number | null
	searchLocation: {lat: number; lng: number} | null | undefined
	showCircle: boolean
}

const locatorBlueHex = requireHexCode('locator-blue')

const circleOptions = {
	strokeColor: locatorBlueHex,
	strokeOpacity: 0.8,
	strokeWeight: 2,
	fillColor: locatorBlueHex,
	fillOpacity: 0.1,
	clickable: false,
	draggable: false,
	editable: false,
	visible: true,
	radius: 3000, // 3km radius
	zIndex: 1,
}

export const Map = ({
	initialZoom,
	minZoom,
	maxZoom,
	onLoad,
	onClick,
	containerStyle,
	containerClassName,
	children,
	radius,
	searchAreaAccentColour,
	searchAreaOpacity,
	searchLocation,
	showCircle,
}: MapProps): JSX.Element => {
	const mapRef = useRef<google.maps.Map | null>(null)

	const handleLoad = (map: google.maps.Map): void => {
		mapRef.current = map
		onLoad(map)
	}

	const radiusInMeters = radius * 1609.34 // converting radius in miles into meters

	return (
		<GoogleMap
			center={{
				lat: searchLocation?.lat ?? 0,
				lng: searchLocation?.lng ?? 0,
			}}
			mapContainerClassName={containerClassName}
			mapContainerStyle={containerStyle}
			onClick={onClick}
			onLoad={handleLoad}
			options={{
				minZoom,
				maxZoom,
				disableDefaultUI: true,
				zoomControl: true,
			}}
			zoom={initialZoom}
		>
			{searchLocation && showCircle ? (
				<Circle
					center={{
						lat: searchLocation.lat,
						lng: searchLocation.lng,
					}}
					options={{
						...circleOptions,
						strokeColor: searchAreaAccentColour,
						fillColor: searchAreaAccentColour,
						fillOpacity: searchAreaOpacity || 0.1,
						radius: radiusInMeters,
					}}
				/>
			) : null}
			{children}
		</GoogleMap>
	)
}
